import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, LinearProgress, MenuItem, Paper, Rating,
  Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField,
  Tooltip, Typography, useMediaQuery, useTheme, GridLegacy as Grid, Autocomplete,
} from '@mui/material';
import { Close, ContentCopy, Download, Edit, Send, Share, Block, WorkspacePremium } from '@mui/icons-material';
import { internshipService, userService } from '../services/api';
import {
  InternshipDetail, InternshipReview, STATUS_META, PROGRESS_META, REVIEW_STATUS_COLOR, OUTCOME_LABEL,
  TERMINATION_REASONS, reviewLabel, fmtDate, fmtScore, apiError,
} from './internshipTypes';
import { downloadInternshipCertificate } from './internshipCertificatePdf';

interface Props {
  internshipId: number | null;
  onClose: () => void;
  onChanged: () => void;
}

interface UserOption { id: number; fullName: string; }

// Mentor / reporting-manager / HR view of one internship: overview + live score, check-ins,
// goals, recommendation & decision, certificates. What's actionable comes from the server's
// can* flags — the UI never re-derives the business rules.
const InternshipDetailDialog: React.FC<Props> = ({ internshipId, onClose, onChanged }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [detail, setDetail] = useState<InternshipDetail | null>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [reviewTarget, setReviewTarget] = useState<InternshipReview | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (internshipId === null) return;
    setLoading(true);
    setError('');
    try {
      setDetail(await internshipService.getById(internshipId));
    } catch (err) {
      setError(apiError(err, 'Failed to load internship.'));
    } finally {
      setLoading(false);
    }
  }, [internshipId]);

  useEffect(() => {
    setTab(0);
    setMessage('');
    setDetail(null);
    load();
  }, [load]);

  // Every mutating endpoint returns the refreshed detail.
  const run = async (action: () => Promise<InternshipDetail>, success: string) => {
    setError('');
    setMessage('');
    try {
      setDetail(await action());
      setMessage(success);
      onChanged();
      return true;
    } catch (err) {
      setError(apiError(err, 'Action failed.'));
      return false;
    }
  };

  const isAdmin = detail?.viewerRole === 'Admin';
  const canMentor = detail?.viewerRole === 'Admin' || detail?.viewerRole === 'Mentor';

  return (
    <Dialog open={internshipId !== null} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 6 }}>
        {detail ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <span>{detail.internName}</span>
            <Typography variant="body2" color="text.secondary">{detail.roleTitle}</Typography>
            <Chip size="small" label={STATUS_META[detail.status]?.label ?? detail.status} color={STATUS_META[detail.status]?.color ?? 'default'} />
          </Stack>
        ) : 'Internship'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
        {loading && !detail && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

        {detail && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
              <Tab label="Overview" />
              <Tab label={`Check-ins (${detail.reviews.filter(r => r.status === 'Submitted').length}/${detail.reviews.length})`} />
              <Tab label={`Goals (${detail.goals.length})`} />
              <Tab label="Evaluation & Decision" />
              <Tab label={`Certificates (${detail.certificates.length})`} />
            </Tabs>

            {tab === 0 && <OverviewTab detail={detail} isAdmin={isAdmin} onEdit={() => setEditOpen(true)} />}
            {tab === 1 && (
              <ReviewsTab
                detail={detail}
                canMentor={canMentor}
                onReview={setReviewTarget}
                onShare={(r) => run(() => internshipService.shareReview(detail.id, r.id), 'Feedback shared with the intern.')}
              />
            )}
            {tab === 2 && <GoalsTab detail={detail} />}
            {tab === 3 && <DecisionTab detail={detail} isAdmin={isAdmin} run={run} />}
            {tab === 4 && <CertificatesTab detail={detail} isAdmin={isAdmin} run={run} />}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {detail && detail.viewerRole !== 'Intern' && (
          detail.shareUrl ? (
            <>
              <Button startIcon={<ContentCopy />} onClick={() => { navigator.clipboard.writeText(detail.shareUrl!); setMessage('Share link copied. Anyone with the link can view this internship (stipend hidden).'); }}>
                Copy share link
              </Button>
              <Button color="error" onClick={() => run(() => internshipService.disableShare(detail.id), 'Share link turned off — the old link no longer works.')}>
                Stop sharing
              </Button>
            </>
          ) : (
            <Button startIcon={<Share />} onClick={async () => {
              const ok = await run(() => internshipService.enableShare(detail.id), 'Share link created.');
              if (ok) setMessage('Share link created — click "Copy share link". No login needed to open it; stipend is hidden.');
            }}>
              Create share link
            </Button>
          )
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {detail && reviewTarget && (
        <MentorReviewDialog
          detail={detail}
          review={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmit={async (data) => {
            const ok = await run(() => internshipService.submitMentorReview(detail.id, reviewTarget.id, data), `${reviewLabel(reviewTarget)} submitted.`);
            if (ok) setReviewTarget(null);
          }}
        />
      )}
      {detail && editOpen && (
        <EditInternshipDialog
          detail={detail}
          onClose={() => setEditOpen(false)}
          onSubmit={async (data) => {
            const ok = await run(() => internshipService.update(detail.id, data), 'Internship updated.');
            if (ok) setEditOpen(false);
          }}
        />
      )}
    </Dialog>
  );
};

// ─── Overview ────────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

export const OverviewTab: React.FC<{ detail: InternshipDetail; isAdmin: boolean; onEdit: () => void }> = ({ detail, isAdmin, onEdit }) => {
  const total = new Date(detail.plannedEndDate).getTime() - new Date(detail.startDate).getTime();
  const elapsed = Math.min(Math.max(Date.now() - new Date(detail.startDate).getTime(), 0), total);
  const pct = total > 0 ? Math.round((elapsed / total) * 100) : 0;
  const isOpen = !['Converted', 'Completed', 'Terminated'].includes(detail.status);
  const s = detail.score;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>Internship details</Typography>
            {isAdmin && <Button size="small" startIcon={<Edit />} onClick={onEdit}>Edit</Button>}
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}><Field label="Intern" value={detail.internName} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Mentor" value={detail.mentorName} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Department" value={detail.department} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Start date" value={fmtDate(detail.startDate)} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Planned end" value={fmtDate(detail.plannedEndDate)} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Actual end" value={fmtDate(detail.actualEndDate)} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Duration" value={`${detail.durationMonths} months${detail.extensionCount ? ` + ${detail.extensionCount} extension` : ''}`} /></Grid>
            <Grid item xs={6} sm={4}><Field label="College" value={detail.college} /></Grid>
            <Grid item xs={6} sm={4}><Field label="Stipend" value={detail.stipend != null ? `₹${detail.stipend.toLocaleString('en-IN')}` : null} /></Grid>
            {detail.notes && <Grid item xs={12}><Field label="Notes" value={detail.notes} /></Grid>}
          </Grid>
          {isOpen && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">Time elapsed</Typography>
                <Typography variant="caption">{detail.daysRemaining} days remaining</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={5}>
        <ScoreCard detail={detail} />
        {s && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Live score: goals {s.goalWeight}% · competencies {s.competencyWeight}% · attendance {s.attendanceWeight}%. Parts without data are left out.
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const ScoreCard: React.FC<{ detail: InternshipDetail }> = ({ detail }) => {
  const s = detail.score;
  if (!s) return null;
  const rows: [string, number | null, string?][] = [
    ['Goal achievement', s.goalScore],
    ['Competencies', s.competencyScore],
    ['Attendance', s.attendanceScore, s.attendancePercent != null ? `${s.attendancePercent.toFixed(1)}%` : undefined],
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Performance score (out of 5)</Typography>
      {rows.map(([label, value, extra]) => (
        <Box key={label} sx={{ mb: 1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">{label}{extra ? ` (${extra})` : ''}</Typography>
            <Typography variant="body2" fontWeight={600}>{fmtScore(value)}</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={value != null ? (value / 5) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" fontWeight={600}>Overall: {fmtScore(s.overallScore)}</Typography>
        {s.suggestedOutcome && <Chip size="small" color="info" label={`Suggests: ${s.suggestedOutcome}`} />}
      </Stack>
    </Paper>
  );
};

// ─── Check-ins ───────────────────────────────────────────────────────────────

export const ReviewsTab: React.FC<{
  detail: InternshipDetail;
  canMentor: boolean;
  onReview: (r: InternshipReview) => void;
  onShare: (r: InternshipReview) => void;
}> = ({ detail, canMentor, onReview, onShare }) => (
  <Stack spacing={1.5}>
    {detail.reviews.map(r => (
      <Paper key={r.id} variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="subtitle2">
              {reviewLabel(r)}{r.cycle > 1 ? ` · extension` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">Due {fmtDate(r.dueDate)}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" label={r.status} color={REVIEW_STATUS_COLOR[r.status]} />
            {r.progressStatus && <Chip size="small" variant="outlined" label={PROGRESS_META[r.progressStatus]?.label} color={PROGRESS_META[r.progressStatus]?.color} />}
            {r.overallRating != null && <Chip size="small" variant="outlined" label={`${r.overallRating.toFixed(1)} / 5`} />}
            {r.mentorSubmittedAt && !r.sharedWithIntern && canMentor && (
              <Button size="small" startIcon={<Share />} onClick={() => onShare(r)}>Share with intern</Button>
            )}
            {r.canSubmitMentor && <Button size="small" variant="contained" onClick={() => onReview(r)}>Submit review</Button>}
            {!r.canSubmitMentor && canMentor && r.reviewType === 'Final' && !r.mentorSubmittedAt && r.status !== 'Cancelled' && (
              <Typography variant="caption" color="text.secondary">Opens {fmtDate(detail.finalWindowOpensOn)}</Typography>
            )}
          </Stack>
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Intern self-assessment</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {r.selfAssessment ?? <i>Not submitted yet</i>}
          </Typography>
        </Box>
        {r.mentorSubmittedAt && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Mentor feedback — {r.reviewedByName} · {fmtDate(r.mentorSubmittedAt)} · {r.sharedWithIntern ? 'shared with intern' : 'not shared yet'}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.mentorFeedback}</Typography>
            {(r.strengths || r.improvements) && (
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {r.strengths && <Grid item xs={12} sm={6}><Field label="Strengths" value={r.strengths} /></Grid>}
                {r.improvements && <Grid item xs={12} sm={6}><Field label="Areas to improve" value={r.improvements} /></Grid>}
              </Grid>
            )}
            {r.mentorRatings && (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mt: 1 }}>
                {detail.competencies.map(c => (
                  <Box key={c.key}>
                    <Typography variant="caption" display="block">{c.label}</Typography>
                    <Rating size="small" readOnly value={r.mentorRatings?.[c.key] ?? 0} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Paper>
    ))}
  </Stack>
);

const MentorReviewDialog: React.FC<{
  detail: InternshipDetail;
  review: InternshipReview;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}> = ({ detail, review, onClose, onSubmit }) => {
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [progressStatus, setProgressStatus] = useState('OnTrack');
  const [feedback, setFeedback] = useState('');
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [share, setShare] = useState(true);
  const [saving, setSaving] = useState(false);

  const complete = detail.competencies.every(c => ratings[c.key] >= 1) && feedback.trim().length > 0;

  const submit = async () => {
    setSaving(true);
    await onSubmit({ ratings, progressStatus, mentorFeedback: feedback, strengths, improvements, shareWithIntern: share });
    setSaving(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{reviewLabel(review)} — {detail.internName}</DialogTitle>
      <DialogContent dividers>
        {review.selfAssessment && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption" display="block">Intern's self-assessment</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{review.selfAssessment}</Typography>
          </Alert>
        )}
        <Typography variant="subtitle2" gutterBottom>Ratings (1 = poor, 5 = excellent)</Typography>
        {detail.competencies.map(c => (
          <Stack key={c.key} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
            <Typography variant="body2">{c.label}</Typography>
            <Rating value={ratings[c.key] ?? 0} onChange={(_, v) => setRatings(prev => ({ ...prev, [c.key]: v ?? 0 }))} />
          </Stack>
        ))}
        <TextField select fullWidth label="Overall progress" value={progressStatus} onChange={e => setProgressStatus(e.target.value)} sx={{ mt: 2 }}>
          {Object.entries(PROGRESS_META).map(([key, meta]) => <MenuItem key={key} value={key}>{meta.label}</MenuItem>)}
        </TextField>
        {progressStatus === 'AtRisk' && <Alert severity="warning" sx={{ mt: 1 }}>HR will be notified that this intern is at risk.</Alert>}
        <TextField fullWidth multiline minRows={3} label="Feedback *" value={feedback} onChange={e => setFeedback(e.target.value)} sx={{ mt: 2 }} />
        <TextField fullWidth multiline minRows={2} label="Strengths" value={strengths} onChange={e => setStrengths(e.target.value)} sx={{ mt: 2 }} />
        <TextField fullWidth multiline minRows={2} label="Areas to improve" value={improvements} onChange={e => setImprovements(e.target.value)} sx={{ mt: 2 }} />
        <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={share} onChange={e => setShare(e.target.checked)} />} label="Share this feedback with the intern now" />
        <Alert severity="info" sx={{ mt: 1 }}>A submitted review is locked and cannot be edited.</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!complete || saving} onClick={submit}>Submit review</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Goals ───────────────────────────────────────────────────────────────────

export const GoalsTab: React.FC<{ detail: InternshipDetail; readOnly?: boolean }> = ({ detail, readOnly }) => (
  <>
    {!readOnly && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Goals are managed in the <b>Goals</b> screen — any goal for {detail.internName} that falls inside the internship period is counted here
        (progress weighted by weightage).
      </Alert>
    )}
    {detail.goals.length === 0 ? (
      <Typography color="text.secondary">No goals set for this internship period yet.</Typography>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Goal</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Weightage</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Progress</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.goals.map(g => (
              <TableRow key={g.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{g.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{g.description}</Typography>
                </TableCell>
                <TableCell>{fmtDate(g.startDate)} – {fmtDate(g.endDate)}</TableCell>
                <TableCell>{g.weightage}%</TableCell>
                <TableCell>
                  <LinearProgress variant="determinate" value={g.progress} sx={{ height: 6, borderRadius: 3 }} />
                  <Typography variant="caption">{g.progress}%</Typography>
                </TableCell>
                <TableCell><Chip size="small" label={g.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </>
);

// ─── Evaluation & decision ───────────────────────────────────────────────────

export const DecisionTab: React.FC<{
  detail: InternshipDetail;
  isAdmin: boolean;
  run: (action: () => Promise<InternshipDetail>, success: string) => Promise<boolean>;
}> = ({ detail, isAdmin, run }) => {
  const finalReview = detail.reviews.find(r => r.reviewType === 'Final' && r.cycle === detail.currentCycle);
  const isOpen = !['Converted', 'Completed', 'Terminated'].includes(detail.status);
  const current = detail.decisions.find(d => d.cycle === detail.currentCycle);
  const endDate = new Date(detail.plannedEndDate);
  const decisionOverdue = isOpen && endDate.getTime() < new Date().setHours(0, 0, 0, 0);

  return (
    <Stack spacing={2}>
      {isOpen && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Final evaluation timeline</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><Field label="Window opens" value={fmtDate(detail.finalWindowOpensOn)} /></Grid>
            <Grid item xs={6} md={3}><Field label="Intern self-assessment & mentor evaluation due" value={fmtDate(finalReview?.dueDate)} /></Grid>
            <Grid item xs={6} md={3}><Field label="HR decision by" value={fmtDate(detail.plannedEndDate)} /></Grid>
            <Grid item xs={6} md={3}>
              <Field label="Current step" value={
                !detail.finalWindowOpen ? 'Waiting for window'
                  : !finalReview?.mentorSubmittedAt ? 'Mentor final evaluation'
                  : !current?.recommendation ? 'Mentor recommendation'
                  : 'HR decision'
              } />
            </Grid>
          </Grid>
          {decisionOverdue && <Alert severity="error" sx={{ mt: 2 }}>The end date has passed without a decision — the intern keeps access until HR decides.</Alert>}
        </Paper>
      )}

      {detail.decisions.map(d => (
        <Paper key={d.id} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {d.cycle === 1 ? 'Original period' : `Extension ${d.cycle - 1}`}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><Field label="Overall score" value={fmtScore(d.overallScore)} /></Grid>
            <Grid item xs={6} sm={3}><Field label="Goals / Competencies" value={`${fmtScore(d.goalScore)} / ${fmtScore(d.competencyScore)}`} /></Grid>
            <Grid item xs={6} sm={3}><Field label="Attendance" value={d.attendancePercent != null ? `${d.attendancePercent.toFixed(1)}%` : '—'} /></Grid>
            <Grid item xs={6} sm={3}><Field label="Score suggests" value={d.suggestedOutcome} /></Grid>
            <Grid item xs={12} sm={6}>
              <Field label={`Mentor recommendation${d.recommendedByName ? ` — ${d.recommendedByName}, ${fmtDate(d.recommendedAt)}` : ''}`}
                value={d.recommendation ? <><b>{d.recommendation}</b> — {d.recommendationReason}</> : 'No recommendation'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={`HR decision${d.decidedByName ? ` — ${d.decidedByName}, ${fmtDate(d.decidedAt)}` : ''}`}
                value={d.decision ? (
                  <>
                    <b>{d.decision}</b>
                    {d.decision === 'Confirm' && ` as ${d.conversionDesignation} (${d.conversionEmploymentType === 'Probation' ? 'probation' : 'full-time'}) from ${fmtDate(d.effectiveDate)}`}
                    {d.decision === 'Extend' && ` by ${d.extensionMonths} month(s) until ${fmtDate(d.newEndDate)}`}
                    {d.decision === 'Complete' && ` on ${fmtDate(d.effectiveDate)}`}
                    {d.decision === 'Terminate' && ` (${d.terminationReason}) on ${fmtDate(d.effectiveDate)}`}
                    {d.decisionReason && ` — ${d.decisionReason}`}
                  </>
                ) : 'Pending'} />
            </Grid>
          </Grid>
        </Paper>
      ))}

      {detail.canRecommend && <RecommendationForm detail={detail} run={run} />}
      {isAdmin && isOpen && <DecisionForm detail={detail} run={run} />}
      {!isOpen && detail.decisions.length === 0 && <Typography color="text.secondary">No decisions recorded.</Typography>}
    </Stack>
  );
};

const RecommendationForm: React.FC<{
  detail: InternshipDetail;
  run: (action: () => Promise<InternshipDetail>, success: string) => Promise<boolean>;
}> = ({ detail, run }) => {
  const existing = detail.decisions.find(d => d.cycle === detail.currentCycle);
  const [recommendation, setRecommendation] = useState(existing?.recommendation ?? detail.score?.suggestedOutcome ?? '');
  const [reason, setReason] = useState(existing?.recommendationReason ?? '');
  const options = ['Confirm', ...(detail.canExtend ? ['Extend'] : []), 'Complete', 'Terminate'];

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {existing?.recommendation ? 'Update your recommendation' : 'Your recommendation'}
      </Typography>
      {detail.score?.suggestedOutcome && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Based on the score ({fmtScore(detail.score.overallScore)} / 5) the system suggests <b>{detail.score.suggestedOutcome}</b>. HR makes the final call.
        </Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
        <TextField select label="Recommendation" value={recommendation} onChange={e => setRecommendation(e.target.value)} sx={{ minWidth: 260 }}>
          {options.map(o => <MenuItem key={o} value={o}>{OUTCOME_LABEL[o]}</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline label="Reason *" value={reason} onChange={e => setReason(e.target.value)} />
      </Stack>
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Button variant="contained" startIcon={<Send />} disabled={!recommendation || !reason.trim()}
          onClick={() => run(() => internshipService.submitRecommendation(detail.id, { recommendation, reason }), 'Recommendation sent to HR.')}>
          Send to HR
        </Button>
      </Box>
    </Paper>
  );
};

const DecisionForm: React.FC<{
  detail: InternshipDetail;
  run: (action: () => Promise<InternshipDetail>, success: string) => Promise<boolean>;
}> = ({ detail, run }) => {
  const recommendation = detail.decisions.find(d => d.cycle === detail.currentCycle)?.recommendation ?? '';
  const outcomes = [
    ...(detail.canDecide ? ['Confirm', ...(detail.canExtend ? ['Extend'] : []), 'Complete'] : []),
    ...(detail.canTerminate ? ['Terminate'] : []),
  ];
  const [decision, setDecision] = useState(outcomes.includes(recommendation) ? recommendation : '');
  const [reason, setReason] = useState('');
  const [designation, setDesignation] = useState('');
  const [conversionType, setConversionType] = useState('');
  const [salary, setSalary] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [extensionMonths, setExtensionMonths] = useState(1);
  const [terminationReason, setTerminationReason] = useState('');
  const [issueCertificate, setIssueCertificate] = useState(true);
  const [saving, setSaving] = useState(false);

  const overriding = decision && decision !== recommendation;
  const reasonRequired = decision === 'Terminate' || overriding;
  const valid = decision
    && (!reasonRequired || reason.trim())
    && (decision !== 'Confirm' || (designation.trim() && conversionType))
    && (decision !== 'Terminate' || terminationReason);

  const submit = async () => {
    setSaving(true);
    await run(() => internshipService.submitDecision(detail.id, {
      decision,
      reason: reason || null,
      effectiveDate: effectiveDate || null,
      extensionMonths: decision === 'Extend' ? extensionMonths : null,
      conversionDesignation: decision === 'Confirm' ? designation : null,
      conversionSalary: decision === 'Confirm' && salary ? Number(salary) : null,
      conversionEmploymentType: decision === 'Confirm' ? conversionType : null,
      terminationReason: decision === 'Terminate' ? terminationReason : null,
      issueCertificate: decision === 'Extend' ? false : issueCertificate,
    }), 'Decision recorded and the intern has been notified.');
    setSaving(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'secondary.main' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>HR final decision</Typography>
      {!detail.canDecide && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Confirm / Extend / Complete become available once the final evaluation window opens ({fmtDate(detail.finalWindowOpensOn)}) and the mentor has submitted the final evaluation.
          Early termination is possible at any time.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth label="Decision" value={decision} onChange={e => { setDecision(e.target.value); setIssueCertificate(true); }}>
            {outcomes.map(o => <MenuItem key={o} value={o}>{OUTCOME_LABEL[o]}{o === recommendation ? ' — recommended' : ''}</MenuItem>)}
          </TextField>
        </Grid>
        {decision === 'Confirm' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Confirm as *" value={conversionType} onChange={e => setConversionType(e.target.value)}
                helperText="Probation first, or directly full-time">
                <MenuItem value="Probation">Probation</MenuItem>
                <MenuItem value="FullTime">Full-time</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="New designation *" value={designation} onChange={e => setDesignation(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Monthly salary (optional)" value={salary} onChange={e => setSalary(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Effective from" InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)}
                helperText="Default: the day after the internship ends" />
            </Grid>
          </>
        )}
        {decision === 'Extend' && (
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Extend by" value={extensionMonths} onChange={e => setExtensionMonths(Number(e.target.value))}>
              {[1, 2, 3].map(m => <MenuItem key={m} value={m}>{m} month{m > 1 ? 's' : ''}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        {decision === 'Complete' && (
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Last working day" InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)}
              helperText={`Default: ${fmtDate(detail.plannedEndDate)}`} />
          </Grid>
        )}
        {decision === 'Terminate' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Termination reason *" value={terminationReason}
                onChange={e => { setTerminationReason(e.target.value); if (e.target.value === 'Misconduct') setIssueCertificate(false); }}>
                {TERMINATION_REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Last working day" InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)}
                helperText="Default: today" />
            </Grid>
          </>
        )}
        {decision && (
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label={reasonRequired ? 'Reason *' : 'Remarks'} value={reason} onChange={e => setReason(e.target.value)}
              helperText={overriding && decision !== 'Terminate' ? "Required because this differs from the mentor's recommendation." : undefined} />
          </Grid>
        )}
        {decision && decision !== 'Extend' && (
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={issueCertificate} disabled={terminationReason === 'Misconduct'} onChange={e => setIssueCertificate(e.target.checked)} />}
              label={decision === 'Terminate' ? 'Issue a participation certificate (only if at least 3 months served, never for misconduct)' : 'Issue completion certificate and email it to the intern'}
            />
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Button variant="contained" color={decision === 'Terminate' ? 'error' : 'primary'} disabled={!valid || saving} onClick={submit}>
          Record decision
        </Button>
      </Box>
    </Paper>
  );
};

// ─── Certificates ────────────────────────────────────────────────────────────

const CertificatesTab: React.FC<{
  detail: InternshipDetail;
  isAdmin: boolean;
  run: (action: () => Promise<InternshipDetail>, success: string) => Promise<boolean>;
}> = ({ detail, isAdmin, run }) => {
  const [revokeId, setRevokeId] = useState<number | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  return (
    <>
      {detail.canIssueCertificate && (
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" startIcon={<WorkspacePremium />} onClick={() => run(() => internshipService.issueCertificate(detail.id), 'Certificate issued and emailed.')}>
            Issue certificate
          </Button>
        </Box>
      )}
      {detail.certificates.length === 0 ? (
        <Typography color="text.secondary">
          No certificate yet. Certificates are issued when the internship is completed or confirmed (or for an eligible early exit).
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Certificate No</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Issued</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.certificates.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.certificateNo}</TableCell>
                  <TableCell>{c.certificateType}</TableCell>
                  <TableCell>{fmtDate(c.issuedAt)}</TableCell>
                  <TableCell>
                    {c.isRevoked
                      ? <Tooltip title={c.revokedReason ?? ''}><Chip size="small" color="error" label="Revoked" /></Tooltip>
                      : <Chip size="small" color="success" label={c.emailSent ? 'Valid · emailed' : 'Valid · email pending'} />}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!c.isRevoked && (
                      <>
                        <Tooltip title="Download PDF"><IconButton size="small" onClick={() => downloadInternshipCertificate(c)}><Download fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Copy verification link"><IconButton size="small" onClick={() => navigator.clipboard.writeText(c.verificationUrl)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                        {isAdmin && <Tooltip title="Resend email"><IconButton size="small" onClick={() => run(() => internshipService.resendCertificate(detail.id, c.id), 'Certificate email sent.')}><Send fontSize="small" /></IconButton></Tooltip>}
                        {isAdmin && <Tooltip title="Revoke"><IconButton size="small" color="error" onClick={() => { setRevokeId(c.id); setRevokeReason(''); }}><Block fontSize="small" /></IconButton></Tooltip>}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={revokeId !== null} onClose={() => setRevokeId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke certificate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>The verification page will show this certificate as revoked. You can issue a new one afterwards.</Typography>
          <TextField fullWidth multiline minRows={2} label="Reason *" value={revokeReason} onChange={e => setRevokeReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeId(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!revokeReason.trim()} onClick={async () => {
            const ok = await run(() => internshipService.revokeCertificate(detail.id, revokeId!, revokeReason), 'Certificate revoked.');
            if (ok) setRevokeId(null);
          }}>Revoke</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Edit (HR) ───────────────────────────────────────────────────────────────

const EditInternshipDialog: React.FC<{
  detail: InternshipDetail;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}> = ({ detail, onClose, onSubmit }) => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [mentor, setMentor] = useState<UserOption | null>({ id: detail.mentorId, fullName: detail.mentorName });
  const [roleTitle, setRoleTitle] = useState(detail.roleTitle);
  const [department, setDepartment] = useState(detail.department ?? '');
  const [college, setCollege] = useState(detail.college ?? '');
  const [stipend, setStipend] = useState(detail.stipend?.toString() ?? '');
  const [notes, setNotes] = useState(detail.notes ?? '');
  const isOpen = !['Converted', 'Completed', 'Terminated'].includes(detail.status);

  useEffect(() => {
    userService.getEmployees().then((data: any[]) => setUsers(data.map(u => ({ id: u.id, fullName: u.fullName })))).catch(() => setUsers([]));
  }, []);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit internship</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Autocomplete
            options={users.filter(u => u.id !== detail.userId)}
            value={mentor}
            disabled={!isOpen}
            getOptionLabel={o => o.fullName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setMentor(v)}
            renderInput={params => <TextField {...params} label="Mentor" />}
          />
          <TextField label="Internship role / title" value={roleTitle} onChange={e => setRoleTitle(e.target.value)} />
          <TextField label="Department" value={department} onChange={e => setDepartment(e.target.value)} />
          <TextField label="College / University" value={college} onChange={e => setCollege(e.target.value)} />
          <TextField type="number" label="Monthly stipend (₹)" value={stipend} onChange={e => setStipend(e.target.value)} />
          <TextField multiline minRows={2} label="Notes (HR only)" value={notes} onChange={e => setNotes(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!roleTitle.trim() || !mentor} onClick={() => onSubmit({
          mentorId: mentor?.id, roleTitle, department, college, notes,
          stipend: stipend === '' ? null : Number(stipend),
        })}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternshipDetailDialog;
